/**
 * Procedural dose grid + ROI helpers — simulates an RTDOSE SOP so the
 * MPR viewport and DVH panel are exercisable end-to-end before a
 * real RT plan lands in the sandbox.
 *
 * Dose distribution is a 3-Gaussian mixture centred on the volume's
 * geometric centre, scaled so the peak reads 7000 cGy (a common
 * prostate / H&N prescription). The two satellite Gaussians simulate
 * a second PTV and a hotspot — enough structure to make the DVH and
 * isodose contours look clinically realistic in screenshots.
 *
 * Regions of Interest (ROIs) are named volumes of the same shape as
 * the underlying grid: PTV_7000, Rectum, Bladder. For the simulation
 * they're ellipsoids anchored at fixed relative offsets.  Replace
 * with the parsed RTSTRUCT SOP when it's available.
 */
import type { Volume } from "@viewer/volume/types";

export interface DoseGrid {
  /** Same dimensions as the image volume, so we can sample a dose
   *  value at any image voxel without resampling. A real RTDOSE SOP
   *  has its own grid + rotation; that rescaling lands later. */
  data:   Float32Array;
  max:    number;       // cGy
  min:    number;
}

export interface Roi {
  id:    string;
  name:  string;
  color: string;
  /** Boolean mask, 1 voxel per bit packed into a Uint8Array. */
  mask:  Uint8Array;
  /** Cached voxel count for % metrics. */
  count: number;
}

export interface RtPlan {
  volume: Volume;
  dose:   DoseGrid;
  rois:   Roi[];
}

function gauss3(x: number, y: number, z: number,
                cx: number, cy: number, cz: number,
                sx: number, sy: number, sz: number): number {
  const dx = (x - cx) / sx, dy = (y - cy) / sy, dz = (z - cz) / sz;
  return Math.exp(-(dx*dx + dy*dy + dz*dz));
}

/** Build a procedural dose grid shaped like the given volume. */
export function simulateDose(v: Volume): DoseGrid {
  const { width: W, height: H, depth: D } = v;
  const data = new Float32Array(W * H * D);
  const cx = W * 0.5, cy = H * 0.55, cz = D * 0.5;
  let max = 0;
  for (let z = 0; z < D; z++) {
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        // Main PTV — broad Gaussian.
        let dose = 7000 * gauss3(x, y, z, cx, cy, cz, W*0.12, H*0.10, D*0.20);
        // Satellite "boost" — tighter.
        dose += 3500 * gauss3(x, y, z, cx + W*0.08, cy - H*0.06, cz, W*0.05, H*0.05, D*0.12);
        // Beam entrance hot-spot — on the anterior surface.
        dose += 1200 * gauss3(x, y, z, cx, cy - H*0.25, cz, W*0.18, H*0.06, D*0.25);
        if (dose < 0) dose = 0;
        data[z * W * H + y * W + x] = dose;
        if (dose > max) max = dose;
      }
    }
  }
  return { data, max, min: 0 };
}

function ellipsoidMask(
  W: number, H: number, D: number,
  cx: number, cy: number, cz: number,
  rx: number, ry: number, rz: number,
): { mask: Uint8Array; count: number } {
  const mask = new Uint8Array(Math.ceil(W * H * D / 8));
  let count = 0;
  for (let z = 0; z < D; z++) {
    const dz = (z - cz) / rz;
    if (Math.abs(dz) > 1) continue;
    for (let y = 0; y < H; y++) {
      const dy = (y - cy) / ry;
      if (Math.abs(dy) > 1) continue;
      for (let x = 0; x < W; x++) {
        const dx = (x - cx) / rx;
        if (dx*dx + dy*dy + dz*dz <= 1) {
          const i = z * W * H + y * W + x;
          mask[i >> 3] |= (1 << (i & 7));
          count++;
        }
      }
    }
  }
  return { mask, count };
}

export function simulateRois(v: Volume): Roi[] {
  const { width: W, height: H, depth: D } = v;
  const mk = (name: string, color: string,
              cx: number, cy: number, cz: number,
              rx: number, ry: number, rz: number): Roi => {
    const { mask, count } = ellipsoidMask(W, H, D, cx, cy, cz, rx, ry, rz);
    return { id: name, name, color, mask, count };
  };
  return [
    mk("PTV_7000", "#ef4444", W*0.5,  H*0.55, D*0.5,  W*0.10, H*0.08, D*0.18),
    mk("Rectum",   "#a855f7", W*0.55, H*0.62, D*0.45, W*0.06, H*0.08, D*0.12),
    mk("Bladder",  "#f59e0b", W*0.48, H*0.48, D*0.50, W*0.08, H*0.07, D*0.08),
  ];
}

/** Return cumulative DVH samples: for each dose bin (cGy), the
 *  fraction of the ROI receiving at least that dose. */
export function dvhCurve(dose: DoseGrid, roi: Roi, bins = 100): { x: number; y: number }[] {
  const step = dose.max / bins;
  const hist = new Float64Array(bins + 1);
  let hit = 0;
  const { data } = dose;
  for (let i = 0; i < data.length; i++) {
    if ((roi.mask[i >> 3] >> (i & 7)) & 1) {
      hit++;
      const b = Math.min(bins, Math.floor(data[i] / step));
      hist[b]++;
    }
  }
  // Cumulative sum from the right — "at least this dose".
  const out: { x: number; y: number }[] = new Array(bins + 1);
  let running = 0;
  for (let b = bins; b >= 0; b--) {
    running += hist[b];
    out[b] = { x: b * step, y: hit > 0 ? running / hit : 0 };
  }
  return out;
}

/** V{threshold}% — percentage of ROI voxels ≥ threshold cGy. */
export function vPct(dose: DoseGrid, roi: Roi, thresholdCGy: number): number {
  let hit = 0, n = 0;
  const { data } = dose;
  for (let i = 0; i < data.length; i++) {
    if ((roi.mask[i >> 3] >> (i & 7)) & 1) {
      n++;
      if (data[i] >= thresholdCGy) hit++;
    }
  }
  return n > 0 ? (hit / n) * 100 : 0;
}
